import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { IntersectionFadeIn } from "@/core/shared/v2/ui/intersection-fade-in";
import { SidebarLayout } from "@/core/shared/v2/ui/sidebar-layout";
import { CatalogList, COMPACT_CARDS_THRESHOLD, FlowSteps, SideCart } from "../components";
import { useRoutes } from "../hooks/use-routes";
import type { BloodTestPackage as BloodTestPackageType } from "../models";
import { useCartStore } from "../store/use-cart-store";
import { useConfigStore, useGetConfig } from "../store/use-config";
import { selectStandardBloodTestPackages } from "../store/use-config/selectors";
import { beginCheckoutEvent, goToUpsellEvent, safeTrackEvent, viewItemListEvent } from "../tracking/events";

export const Catalog = () => {
  const { t } = useTranslation();
  const config = useGetConfig();
  const standardBloodTestPackages = useConfigStore(selectStandardBloodTestPackages);
  const { products, addProduct, getStandardProduct } = useCartStore();
  const { goToSelectTime, goToUpsell, goToPhoneVerification } = useRoutes();

  // Stabilize the dependency to prevent redundant view_item_list fires (matches REV-92 pattern).
  const packageUuids = standardBloodTestPackages.map((p) => p.uuid).join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: see packageUuids comment above
  useEffect(() => {
    if (standardBloodTestPackages.length === 0) return;
    safeTrackEvent(
      () =>
        viewItemListEvent(
          standardBloodTestPackages.map((p) => ({
            ...p,
            productType: "blood-test-package" as const,
            quantity: 1,
            item_list_id: "listing" as const,
          })),
        ),
      "view_item_list",
      "catalog",
    );
  }, [packageUuids]);

  const handleSelect = (btPackage: BloodTestPackageType, index: number) => {
    addProduct(btPackage, "listing", index);
  };

  const handleContinue = () => {
    const standardProduct = getStandardProduct();
    if (!standardProduct) return;

    if (config.withCalendar) return goToSelectTime();
    if (standardProduct.upsellBloodTestPackageUuids?.length) {
      safeTrackEvent(() => goToUpsellEvent(), "go_to_upsell", "catalog handleContinue");
      return goToUpsell();
    }

    // Direct path to phone-verification (no calendar, no upsell) — checkout begins here.
    safeTrackEvent(
      () =>
        beginCheckoutEvent(
          [
            {
              ...standardProduct,
              productType: "blood-test-package" as const,
              quantity: 1,
              item_list_id: standardProduct.item_list_id ?? "listing",
            },
          ],
          { value: Number(standardProduct.grossPrice) },
          { numberOfProducts: 1 },
        ),
      "begin_checkout",
      "catalog handleContinue",
    );
    goToPhoneVerification();
  };

  const hasStandardSelected = products.some((product) => !product.isUpsell);

  return (
    <IntersectionFadeIn className="mx-auto w-full max-w-[1376px] px-4 pt-8 pb-14 md:px-8 md:pb-20 lg:px-12">
      <FlowSteps />
      <div className="mb-6 flex flex-col items-start">
        <h1 className="font-heading font-serif text-2xl lg:text-4xl">
          {t("GROUP_ACTIONS_ORDERING.BLOOD_TEST_PACKAGE.SELECT_OFFER")}
        </h1>
      </div>
      <SidebarLayout>
        <CatalogList
          simplified={standardBloodTestPackages.length > COMPACT_CARDS_THRESHOLD}
          bloodTestPackages={standardBloodTestPackages}
          onSelect={handleSelect}
          isProductInCart={(pkg) => products.some((product) => product.uuid === pkg.uuid)}
        />
        <SideCart onNextStep={handleContinue} ctaDisabled={!hasStandardSelected} />
      </SidebarLayout>
    </IntersectionFadeIn>
  );
};
